import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import s from './styles.module.css'
import cx from 'classnames'
import { useMutation, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import useEligibilityUpdates from 'hooks/use-eligibility-updates'
import useCurrentUser from 'hooks/use-current-user'
import { diagnosesByBmi, getBmi } from 'utilities/bmi'
import { INITIAL_SERVICE_TYPE_ID, STATES } from 'constants/bridge'
import { checkEligibility as checkEligibilityMutation } from './mutations.graphql'
import { createClientTrackingConsent as createClientTrackingConsentMutation } from 'components/shared/tracking-consent-disclosure/mutations.graphql'
import { grantTrackingConsent } from 'utilities/tracking-consent'
import { recordTrackingConsent } from 'components/shared/tracking-consent-disclosure/record-tracking-consent'
import { getCurrentUser } from 'providers/current-user-provider/queries.graphql'
import { STEPS } from 'components/sign-up/insurance-coverage-flow/constants'
import { useSurveyStore } from 'stores/survey-store'
import { getCopay } from 'utilities/insurance/copay'
import CoverageResult from 'components/insurance-coverage/coverage-result'
import { lpVariantFrom } from 'components/survey/lp-variants'
import Tile1 from 'components/shared/survey-question/insurance-covered-loading/assets/tile1.png'
import Tile2 from 'components/shared/survey-question/insurance-covered-loading/assets/tile2.png'
import Tile3 from 'components/shared/survey-question/insurance-covered-loading/assets/tile3.png'
import Tile4 from 'components/shared/survey-question/insurance-covered-loading/assets/tile4.png'

// Self-correcting fallback: poll the DB-backed eligibility status (driven fast
// by the backend session poller) so the screen advances even if the Pusher
// event is dropped. This flow has no countdown fallback, so the poll is the
// safety net when Bridge webhooks are down.
const ELIGIBILITY_POLL_INTERVAL_MS = 2500

export const STATE_RESPONSES = {
  1137: 'AL',
  1138: 'AK',
  1139: 'AZ',
  1140: 'AR',
  1188: 'CA',
  1141: 'CO',
  1142: 'CT',
  1143: 'DE',
  1216: 'DC',
  1145: 'FL',
  1146: 'GA',
  1147: 'HI',
  1148: 'ID',
  1149: 'IN',
  1150: 'IA',
  1151: 'IL',
  1152: 'KS',
  1153: 'KY',
  1154: 'LA',
  1155: 'ME',
  1156: 'MD',
  1157: 'MA',
  1158: 'MI',
  1159: 'MN',
  1160: 'MS',
  1161: 'MT',
  1162: 'NE',
  1163: 'MO',
  1164: 'NV',
  1165: 'NH',
  1166: 'NJ',
  1167: 'NM',
  1168: 'NY',
  1169: 'NC',
  1170: 'ND',
  1171: 'OH',
  1172: 'OK',
  1173: 'OR',
  1174: 'PA',
  1175: 'RI',
  1176: 'SC',
  1177: 'SD',
  1178: 'TN',
  1179: 'TX',
  1180: 'UT',
  1181: 'VT',
  1182: 'VA',
  1183: 'WA',
  1184: 'WI',
  1185: 'WY',
  1186: 'WV',
}

interface InsuranceCoveredLoadingProps {
  question: {
    id: number | string
    text: string
    isRequired?: boolean
    type: string
    // The coverage preview in an inline landing-page variant
    // (components/survey/lp-variants): a quick check before member ID, height
    // and weight, shown without a co-pay.
    softCheck?: boolean
  }
  // RHF mode (not used, but included for consistency)
  field?: {
    value: any
    onChange: (value: any) => void
    onBlur: () => void
  }
  control?: any
  errors?: any
  // Zustand mode (not used, but included for consistency)
  value?: any
  error?: string
  onChange?: (value: any) => void
  // Shared props
  clientId?: number
  responseByQuestionId: Record<number, any[]>
  allQuestions: Array<{ id: number | string }>
  nextQuestionId?: number | string
}

// Steps that should not block sign-up. A provider-side ERROR (e.g. an
// eligibility-check timeout) or a POLICY_INVALID that persists after a retry
// shouldn't trap the client on a dead-end screen — resolve both to the eligible
// path so they continue through the sign-up flow and coverage is reconciled later.
const resolveEligibilityStep = (
  currentStep: string,
  retryCount?: number
): string => {
  if (currentStep === STEPS.ERROR) {
    return STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE
  }
  if (currentStep === STEPS.POLICY_INVALID && (retryCount ?? 0) >= 1) {
    return STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE
  }
  return currentStep
}

/**
 * Runs the sign-up eligibility check and shows its result. As the soft-check
 * preview it stops after the provider check and hides the co-pay.
 */
const InsuranceCoveredLoading: React.FC<InsuranceCoveredLoadingProps> = ({
  question,
  clientId,
  responseByQuestionId,
  allQuestions,
  nextQuestionId,
}) => {
  const router = useRouter()
  const currentUserPayload = useCurrentUser()
  const me = currentUserPayload?.data?.me
  const [checkStarted, setCheckStarted] = useState(false)
  // Mirrors checkStarted, but set the moment a check starts. Clearing a prior
  // result updates the survey store, which re-renders this step before
  // setCheckStarted(true) lands; that render must not start a second check.
  const checkStartedRef = useRef(false)
  // True once the check has actually entered a pending state; gates the poll
  // fallback so it stays inert when eligibility resolves synchronously.
  const [isPending, setIsPending] = useState(false)
  // Set once any path (mutation, Pusher, or the poll) reaches a terminal
  // result; turns the poll off.
  const [resolved, setResolved] = useState(false)
  // Drives the poll: true only while a check is genuinely pending (fresh or
  // resumed) and unresolved, so we don't poll before a check exists or after
  // it's done. Updated by an effect below — it can't be derived inline because
  // it depends on the query result that the poll itself feeds.
  const [shouldPoll, setShouldPoll] = useState(false)
  // Id of the eligibility-status row the in-flight check is producing. The poll
  // only applies a terminal status whose id matches this, so a cached prior
  // terminal row served by getCurrentUser can't be mistaken for the new result.
  const [awaitingStatusId, setAwaitingStatusId] = useState<string | null>(null)

  // Zustand store hooks
  const setEligibilityStatus = useSurveyStore(
    state => state.setEligibilityStatus
  )
  const setEligibilityData = useSurveyStore(state => state.setEligibilityData)
  const setCopay = useSurveyStore(state => state.setCopay)
  const setPolicyMetadata = useSurveyStore(state => state.setPolicyMetadata)
  const setPolicyRetryCount = useSurveyStore(state => state.setPolicyRetryCount)
  const storedEligibilityInputParams = useSurveyStore(
    state => state.eligibilityInputParams
  )
  const setLatestEligibilityParamsFromDB = useSurveyStore(
    state => state.setLatestEligibilityParamsFromDB
  )
  const latestEligibilityParamsFromDB = useSurveyStore(
    state => state.latestEligibilityParamsFromDB
  )

  const header = question?.text || 'Checking Insurance Eligibility'
  const isPreview = Boolean(question?.softCheck)
  const [checkEligibility] = useMutation(checkEligibilityMutation)

  // Get eligibility status from Zustand early to check if there's already a result
  // Use individual selectors to prevent unnecessary re-renders
  // Each selector only triggers when that specific value changes
  const existingEligibilityStatus = useSurveyStore(
    state => state.eligibilityStatus
  )
  const existingEligibilityData = useSurveyStore(state => state.eligibilityData)
  const existingCopay = useSurveyStore(state => state.copay)
  const existingPolicyMetadata = useSurveyStore(state => state.policyMetadata)

  // Query current user to get client eligibility statuses and client data.
  // Polls only while a check is genuinely pending — the backend session
  // poller drives the DB within seconds when Bridge webhooks are down, so
  // this advances the screen even if the Pusher event never arrives.
  const {
    data: meData,
    loading: meDataLoading,
    refetch,
  } = useQuery(getCurrentUser, {
    skip: !clientId,
    fetchPolicy: 'cache-and-network',
  })

  // Drive polling with a recursive timeout + refetch() rather than Apollo's
  // pollInterval/startPolling: neither reliably keeps issuing requests here
  // once started (the query fires once, then goes silent), which is why the
  // eligibility screen used to hang forever waiting for a status that never
  // re-queries. This CurrentUserProvider's query is also mounted higher up
  // in the tree watching the same document, and manual refetch() sidesteps
  // whatever interaction breaks Apollo's own poll timer for this watcher.
  useEffect(() => {
    if (!shouldPoll || !clientId) return

    let cancelled = false
    let timeout: ReturnType<typeof setTimeout>

    const poll = async () => {
      try {
        await refetch()
      } catch {
        // A transient query failure should not stop the next eligibility check.
      }

      if (!cancelled) {
        timeout = setTimeout(poll, ELIGIBILITY_POLL_INTERVAL_MS)
      }
    }

    timeout = setTimeout(poll, ELIGIBILITY_POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [clientId, refetch, shouldPoll])

  const { diagnoses, latestEligibilityStatus } = useMemo(() => {
    // Helper to get first response value (most recent)
    const getResponseValue = (questionId: number) => {
      const responses = responseByQuestionId[questionId]
      return responses && responses.length > 0 ? responses[0] : null
    }

    const heightResponse = getResponseValue(10)
    const weightResponse = getResponseValue(9)
    const height = heightResponse?.responseValue
    const weight = weightResponse?.responseValue
    const bmi = getBmi({
      height: height || undefined,
      weight: weight || undefined,
    })
    const bmiDiagnoses = bmi ? diagnosesByBmi({ bmi }) : []
    const diagnosisResponse = getResponseValue(5425)
    const previouslyAnsweredDiagnoses =
      diagnosisResponse?.responseValue?.split(',') || []
    const diagnoses = [...bmiDiagnoses, ...previouslyAnsweredDiagnoses].filter(
      Boolean
    )

    return {
      diagnoses,
      latestEligibilityStatus: R.pathOr(
        null,
        ['me', 'user', 'client', 'latestClientEligibilityStatus'],
        meData || me
      ),
    }
  }, [responseByQuestionId, meData, me])

  // Check whether the latest eligibility status is pending
  const hasPendingEligibility =
    latestEligibilityStatus?.currentStep === 'POLICY_PENDING' ||
    latestEligibilityStatus?.currentStep === 'SERVICE_ELIGIBILITY_PENDING'

  // Poll only while a check is genuinely pending (freshly kicked or resumed on
  // load) and not yet resolved. Re-evaluated whenever those inputs change, so a
  // params-change re-check (which resets `resolved`) re-arms the poll.
  useEffect(() => {
    setShouldPoll(!resolved && (isPending || hasPendingEligibility))
  }, [resolved, isPending, hasPendingEligibility])

  // Follow the row we're waiting on: while the latest status is pending, that's
  // the in-flight check (covers resume on load and a retry's fresh row), so keep
  // awaitingStatusId pointed at its id. The watcher then applies that row's
  // resolution rather than a stale prior one.
  useEffect(() => {
    if (hasPendingEligibility && latestEligibilityStatus?.id) {
      setAwaitingStatusId(latestEligibilityStatus.id)
    }
  }, [hasPendingEligibility, latestEligibilityStatus?.id])

  // Find next question ID - use prop if provided, otherwise find next in sequence
  const nextQuestion = useMemo(() => {
    if (nextQuestionId) {
      return allQuestions.find(q => String(q.id) === String(nextQuestionId))
    }
    const currentIndex = allQuestions.findIndex(
      q => String(q.id) === String(question.id)
    )
    if (currentIndex >= 0 && currentIndex < allQuestions.length - 1) {
      return allQuestions[currentIndex + 1]
    }
    return null
  }, [allQuestions, question.id, nextQuestionId])

  // Handle eligibility status update - store in Zustand but don't route immediately
  // Routing will happen when user clicks continue on the result
  const handleEligibilityStatus = useCallback(
    (eligibilityData: any, status: string, retryCount?: number) => {
      // Map provider-side errors / unverifiable policies to the eligible path so
      // the client is let through the sign-up flow instead of hitting a dead end.
      const resolvedStatus = resolveEligibilityStep(status, retryCount)
      // Store eligibility data in Zustand store
      setLatestEligibilityParamsFromDB(eligibilityData?.eligibilityParams)
      setEligibilityStatus(resolvedStatus)
      setEligibilityData(eligibilityData?.serviceEligibilityMetadata)
      setPolicyMetadata(eligibilityData?.policyMetadata)
      setPolicyRetryCount(retryCount ?? null)
      // Terminal result reached via some path — stop the poll fallback.
      setResolved(true)

      // Calculate copay if eligible
      if (
        resolvedStatus === STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE ||
        resolvedStatus === STEPS.SERVICE_ELIGIBILITY_ELIGIBLE
      ) {
        const patientResponsibility =
          eligibilityData?.serviceEligibilityMetadata
            ?.conditionalPatientResponsibilities?.[0]?.patientResponsibility ||
          eligibilityData?.serviceEligibilityMetadata?.patientResponsibility
        setCopay(
          getCopay(
            patientResponsibility,
            eligibilityData?.eligibilityParams?.clinicalInfo?.diagnoses
          )
        )
      }
    },
    [
      setCopay,
      setEligibilityData,
      setEligibilityStatus,
      setLatestEligibilityParamsFromDB,
      setPolicyMetadata,
      setPolicyRetryCount,
      setResolved,
    ]
  )

  // Get eligibility status and data from Zustand early (before callbacks that use them)
  const eligibilityStatus = existingEligibilityStatus
  const eligibilityData = existingEligibilityData
  const copay = existingCopay
  const policyMetadata = existingPolicyMetadata

  const [createClientTrackingConsent] = useMutation(
    createClientTrackingConsentMutation
  )

  // Handle continue from result - route to next question or verify-coverage
  // Memoize to prevent recreation on every render
  const handleContinueFromResult = useCallback(() => {
    // Continue is the consent click: grant client-side, record server-side.
    // Record only when the grant succeeded (GPC suppresses both); every
    // no-record outcome revokes the grant — see recordTrackingConsent.
    if (grantTrackingConsent()) {
      recordTrackingConsent(
        createClientTrackingConsent,
        clientId ??
          meData?.me?.user?.client?.id ??
          currentUserPayload?.data?.me?.user?.client?.id,
        'insurance-coverage'
      )
    }

    const currentStatus = useSurveyStore.getState().eligibilityStatus

    // If eligible and skipMembershipId is true, go to verify-coverage
    if (currentStatus === STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE) {
      // // Read skipMembershipId from Zustand store instead of responseByQuestionId
      // const skipMembershipId =
      //   useSurveyStore.getState().eligibilityInputParams?.skipMembershipId
      // if (!skipMembershipId) {
      //   router.push(`/verify-coverage?insuranceCovered=true&recheck=true`)
      //   return
      // }
    }

    // For ineligible/invalid cases, go to verify-coverage
    if (
      currentStatus === STEPS.UNDER_18_INELIGIBLE ||
      currentStatus === STEPS.PROVIDER_ELIGIBILITY_INELIGIBLE ||
      currentStatus === STEPS.SERVICE_ELIGIBILITY_INELIGIBLE ||
      currentStatus === STEPS.SERVICE_ELIGIBILITY_INEFFECTIVE ||
      currentStatus === STEPS.SERVICE_ELIGIBILITY_UNINSURED ||
      currentStatus === STEPS.POLICY_INVALID
    ) {
      router.push({
        pathname: '/verify-coverage',
        query: { ...router.query, insuranceCovered: true, recheck: true },
      })
      return
    }

    // For eligible cases, go to next question (stays on /sign-up)
    if (
      currentStatus === STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE ||
      currentStatus === STEPS.SERVICE_ELIGIBILITY_ELIGIBLE
    ) {
      if (nextQuestion) {
        router.push(
          {
            pathname: '/sign-up',
            query: { ...router.query, questionId: nextQuestion.id },
          },
          undefined,
          { shallow: true }
        )
      }
    }
  }, [
    router,
    nextQuestion,
    clientId,
    meData,
    currentUserPayload,
    createClientTrackingConsent,
  ])

  // Compare current input params with latestEligibilityParamsFromDB to detect changes
  // latestEligibilityParamsFromDB contains the eligibility params from the last eligibility check
  const replacingPreview =
    !isPreview && !!latestEligibilityParamsFromDB?.onlySoftCheck
  const hasInputParamsChanged = useMemo(() => {
    if (!latestEligibilityParamsFromDB) return false

    // Extract values from latestEligibilityParamsFromDB for comparison
    // Note: eligibilityParams has nested structure (person.firstName, location.state, etc.)
    const lastCheckPayerId = latestEligibilityParamsFromDB?.payerId || null
    let lastCheckMemberId = latestEligibilityParamsFromDB?.memberId || null
    // Ignore 'SANDBOX' memberId in development/staging - it's a placeholder when no memberId is passed
    if (lastCheckMemberId === 'SANDBOX') {
      lastCheckMemberId = null
    }
    const lastCheckState =
      latestEligibilityParamsFromDB?.location?.state || null
    const lastCheckFirstName =
      latestEligibilityParamsFromDB?.person?.firstName || null
    const lastCheckLastName =
      latestEligibilityParamsFromDB?.person?.lastName || null
    // Extract just the date part (YYYY-MM-DD) from ISO string like "1986-09-17T00:00:00.000Z"
    const lastCheckDateOfBirth = latestEligibilityParamsFromDB?.person
      ?.dateOfBirth
      ? latestEligibilityParamsFromDB.person.dateOfBirth.split('T')[0]
      : null
    const lastCheckDiagnoses =
      latestEligibilityParamsFromDB?.clinicalInfo?.diagnoses || []

    // The full result never reuses the preview's quick check.
    if (replacingPreview) return true

    // Compare all relevant fields. The preview sends no member ID or
    // diagnoses, so answers given after it never make it look outdated (a
    // client who steps back to it would otherwise re-run it on every result).
    return (
      lastCheckPayerId !== storedEligibilityInputParams?.payerId ||
      lastCheckState !== storedEligibilityInputParams?.state ||
      lastCheckFirstName !== storedEligibilityInputParams?.firstName ||
      lastCheckLastName !== storedEligibilityInputParams?.lastName ||
      lastCheckDateOfBirth !== storedEligibilityInputParams?.dateOfBirth ||
      (!isPreview &&
        (lastCheckMemberId !== storedEligibilityInputParams?.memberId ||
          // Sort copies, not the originals — lastCheckDiagnoses can come
          // straight from Apollo's cache, which Object.freezes results in
          // dev, and Array.prototype.sort mutates in place (throws on a
          // frozen array).
          JSON.stringify([...lastCheckDiagnoses].sort()) !==
            JSON.stringify(
              [...(storedEligibilityInputParams?.diagnoses || [])].sort()
            )))
    )
  }, [
    latestEligibilityParamsFromDB,
    storedEligibilityInputParams,
    replacingPreview,
    isPreview,
  ])

  // Check if there's already a final eligibility status (not pending)
  // But also check if input params have changed - if so, we need to re-run
  const hasExistingFinalStatus = useMemo(() => {
    if (!existingEligibilityStatus) return false
    // If input params changed, don't consider it a valid existing status
    if (hasInputParamsChanged) return false
    return (
      existingEligibilityStatus !== STEPS.POLICY_PENDING &&
      existingEligibilityStatus !== STEPS.SERVICE_ELIGIBILITY_PENDING
    )
  }, [existingEligibilityStatus, hasInputParamsChanged])

  // Latches true once a check is genuinely pending, so the apply watcher below
  // isn't gated out the instant the status resolves (hasPendingEligibility flips
  // false) — which would skip applying the very result we were waiting for.
  const wasWaitingForEligibilityRef = useRef(false)
  if (
    (isPending || hasPendingEligibility) &&
    !hasExistingFinalStatus &&
    !wasWaitingForEligibilityRef.current
  ) {
    wasWaitingForEligibilityRef.current = true
  }

  // When the polled DB status reaches a terminal step, apply it exactly like the
  // Pusher handler does (store in Zustand -> showResult -> CoverageResult).
  useEffect(() => {
    if (
      !wasWaitingForEligibilityRef.current ||
      hasExistingFinalStatus ||
      hasInputParamsChanged ||
      !latestEligibilityStatus ||
      // Only apply the row the in-flight check is producing — not a cached prior
      // terminal row that getCurrentUser may still be serving.
      latestEligibilityStatus.id !== awaitingStatusId
    ) {
      return
    }
    const step = latestEligibilityStatus.currentStep
    if (
      step === STEPS.POLICY_PENDING ||
      step === STEPS.SERVICE_ELIGIBILITY_PENDING
    ) {
      return
    }
    // Mirror checkPolicyStatus's retry condition: a POLICY_INVALID with
    // retryCount 0 and a TIMEOUT/ERROR policy error is about to be auto-retried
    // by the backend (which commits the invalid step, then creates a fresh
    // pending record). The poll reads the DB directly, so it can catch that
    // transient invalid — don't surface it as final; keep polling for the
    // retry's result. A non-retryable invalid (no such error) is genuinely
    // terminal and still applied below.
    const policyErrors: Array<{ code?: string }> =
      latestEligibilityStatus.policyMetadata?.errors ?? []
    const willAutoRetry =
      step === STEPS.POLICY_INVALID &&
      (latestEligibilityStatus.retryCount ?? 0) < 1 &&
      policyErrors.some(
        e => e?.code === 'TIMEOUT' || (e?.code ?? '').includes('ERROR')
      )
    if (willAutoRetry) return
    const status =
      step === STEPS.POLICY_INVALID &&
      (latestEligibilityStatus.retryCount ?? 0) >= 1
        ? STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE
        : step
    handleEligibilityStatus(
      latestEligibilityStatus,
      status,
      latestEligibilityStatus.retryCount
    )
  }, [
    latestEligibilityStatus,
    hasExistingFinalStatus,
    hasInputParamsChanged,
    awaitingStatusId,
    handleEligibilityStatus,
  ])

  useEffect(() => {
    // If input params changed and we have an existing status, clear it and re-run
    if (hasInputParamsChanged && existingEligibilityStatus) {
      setEligibilityStatus(null)
      setEligibilityData(null)
      setPolicyMetadata(null)
      setPolicyRetryCount(null)
      setCopay(null)
      setCheckStarted(false) // Reset check started flag to allow re-run
      checkStartedRef.current = false
      // Re-arm the poll latches so the re-check's pending state restarts the
      // fallback poll (otherwise resolved/isPending stay latched from the prior
      // result and the poll never resumes).
      setResolved(false)
      setIsPending(false)
      setAwaitingStatusId(null)
      wasWaitingForEligibilityRef.current = false
      // Forget the preview's quick check too, so the full check's pending
      // result is not mistaken for another change of inputs.
      if (replacingPreview) setLatestEligibilityParamsFromDB(null)
    }

    // If there's already a final status and params haven't changed, don't start a new check
    if (hasExistingFinalStatus) {
      return
    }

    const startCheck = async () => {
      if (
        !clientId ||
        !storedEligibilityInputParams?.payerId ||
        !storedEligibilityInputParams?.state ||
        !storedEligibilityInputParams?.firstName ||
        !storedEligibilityInputParams?.lastName ||
        !storedEligibilityInputParams?.dateOfBirth
      )
        return

      checkStartedRef.current = true
      setCheckStarted(true)

      const {
        payerId,
        state,
        firstName,
        lastName,
        dateOfBirth,
        memberId,
        skipMembershipId,
      } = storedEligibilityInputParams || {}

      await checkEligibility({
        variables: {
          input: {
            clientId: clientId!,
            payerId,
            location: {
              state,
            },
            person: {
              firstName,
              lastName,
              dateOfBirth,
            },
            serviceTypeId: INITIAL_SERVICE_TYPE_ID,
            // The preview checks the plan and state only; member ID and
            // diagnoses come later, for the full result.
            memberId: isPreview ? undefined : memberId,
            skipMembershipId: isPreview ? true : skipMembershipId,
            ...(isPreview && { onlySoftCheck: true }),
            clinicalInfo: {
              diagnoses: isPreview ? [] : diagnoses,
            },
          },
        },
        onCompleted: (data: any) => {
          if (
            data.checkEligibility.currentStep === STEPS.POLICY_PENDING ||
            data.checkEligibility.currentStep ===
              STEPS.SERVICE_ELIGIBILITY_PENDING
          ) {
            // Genuinely waiting on Bridge — start the self-correcting poll that
            // races the backend session poller and Pusher. Track this row's id
            // so the poll waits for THIS check, not a cached prior result.
            setAwaitingStatusId(data.checkEligibility.id)
            setIsPending(true)
            return
          }

          // Pass the raw currentStep; handleEligibilityStatus resolves the
          // let-through mapping (ERROR / POLICY_INVALID-after-retry → eligible).
          handleEligibilityStatus(
            data.checkEligibility,
            data.checkEligibility.currentStep,
            data.checkEligibility.retryCount
          )
        },
      })
    }

    if (
      !hasPendingEligibility &&
      !checkStarted &&
      !checkStartedRef.current &&
      clientId &&
      !hasExistingFinalStatus
    ) {
      startCheck()
    }
  }, [
    checkEligibility,
    checkStarted,
    clientId,
    diagnoses,
    existingEligibilityStatus,
    handleEligibilityStatus,
    hasExistingFinalStatus,
    hasInputParamsChanged,
    hasPendingEligibility,
    isPreview,
    replacingPreview,
    setCopay,
    setLatestEligibilityParamsFromDB,
    setEligibilityData,
    setEligibilityStatus,
    setPolicyMetadata,
    setPolicyRetryCount,
    storedEligibilityInputParams,
  ])

  // Get client for CoverageResult - prioritize meData query result, fallback to currentUserPayload
  const client = useMemo(() => {
    // Try meData first (from the query we're running)
    if (meData?.me?.user?.client) {
      return meData.me.user.client
    }
    // Fallback to currentUserPayload
    if (currentUserPayload?.data?.me?.user?.client) {
      return currentUserPayload.data.me.user.client
    }
    return null
  }, [meData, currentUserPayload])

  // // 30 second timeout fallback - only if no status yet
  // useCountdown(0, 30, () => {
  //   if (!eligibilityStatus && nextQuestion) {
  //     router.push(
  //       {
  //         pathname: '/sign-up',
  //         query: { questionId: nextQuestion.id },
  //       },
  //       undefined,
  //       { shallow: true }
  //     )
  //   }
  // })

  // Listen for Pusher updates
  useEligibilityUpdates(clientId, (data: any) => {
    if (
      data.currentStep === STEPS.POLICY_PENDING ||
      data.currentStep === STEPS.SERVICE_ELIGIBILITY_PENDING
    ) {
      return
    }

    // Format data to match onCompleted structure
    const eligibilityData = {
      serviceEligibilityMetadata: data.serviceEligibilityMetadata,
      policyMetadata: data.policyMetadata,
      eligibilityParams: data.eligibilityParams,
    }

    // Pass the raw currentStep; handleEligibilityStatus resolves the
    // let-through mapping (ERROR / POLICY_INVALID-after-retry → eligible).
    handleEligibilityStatus(eligibilityData, data.currentStep, data.retryCount)
  })

  // Show result if we have a final status - memoize to prevent unnecessary recalculations
  const showResult = useMemo(
    () =>
      // On the full result, the preview's quick check is about to be
      // replaced: keep the loading screen until the full check answers.
      !replacingPreview &&
      (eligibilityStatus === STEPS.UNDER_18_INELIGIBLE ||
        eligibilityStatus === STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE ||
        eligibilityStatus === STEPS.PROVIDER_ELIGIBILITY_INELIGIBLE ||
        eligibilityStatus === STEPS.SERVICE_ELIGIBILITY_ELIGIBLE ||
        eligibilityStatus === STEPS.SERVICE_ELIGIBILITY_INELIGIBLE ||
        eligibilityStatus === STEPS.SERVICE_ELIGIBILITY_INEFFECTIVE ||
        eligibilityStatus === STEPS.SERVICE_ELIGIBILITY_UNINSURED ||
        eligibilityStatus === STEPS.NO_COACH_MATCHES ||
        eligibilityStatus === STEPS.POLICY_INVALID ||
        eligibilityStatus === STEPS.ERROR),
    [eligibilityStatus, replacingPreview]
  )

  // Memoize the insurance company name to prevent unnecessary re-renders
  // Must be called before any early returns (React Hook rules)
  const insuranceCompanyName = useMemo(
    () => policyMetadata?.payer?.name,
    [policyMetadata?.payer?.name]
  )

  // An inline landing-page variant's sign-up (?lp=v1|v2) gets its own result
  // copy; the carrier comes from the payer answer (the hand-off saves its name).
  const lpVariant = lpVariantFrom(router.query.lp)
  const payerAnswers = useSurveyStore(state => state.answers['1586'])
  const variantCopy = useMemo(() => {
    if (!lpVariant) return undefined
    const stateCode = storedEligibilityInputParams?.state
    return {
      firstName: storedEligibilityInputParams?.firstName,
      carrier:
        payerAnswers?.[payerAnswers.length - 1]?.metadata?.name ||
        insuranceCompanyName,
      stateName: STATES.find(state => state.value === stateCode)?.label,
    }
  }, [
    lpVariant,
    storedEligibilityInputParams,
    payerAnswers,
    insuranceCompanyName,
  ])

  // Memoize the CoverageResult props to prevent unnecessary re-renders
  // Must be called before any early returns (React Hook rules)
  const coverageResultProps = useMemo(
    () => ({
      client,
      insuranceCompany: insuranceCompanyName,
      onContinue: handleContinueFromResult,
      eligibilityStatus: eligibilityStatus!,
      eligibilityData,
      policyMetadata,
      copay: copay || 0,
      showMemberIdSkip: true,
      showCopay: !isPreview,
      variantCopy,
      referredByCoachEligible: false,
      checkEligibility: async () => {
        // This is handled by the component's own checkEligibility logic
        // Pass a no-op function since the component manages its own eligibility checks
      },
      isLoading: false,
    }),
    [
      client,
      insuranceCompanyName,
      handleContinueFromResult,
      eligibilityStatus,
      eligibilityData,
      policyMetadata,
      copay,
      isPreview,
      variantCopy,
    ]
  )

  // Show result if we have a final status
  if (showResult) {
    // Wait for client data only while the query is actively loading
    if (!client && meDataLoading) {
      return (
        <div>
          <h2 className={s.header}>{header}</h2>
          <div className={s.animationContainer}>
            <div className={s.row}>
              <div className={cx(s.image, s.image1)}>
                <Image
                  src={Tile1}
                  alt="Insurance Covered Loading"
                  width={85}
                  height={85}
                />
              </div>
              <div className={cx(s.image, s.image2)}>
                <Image
                  src={Tile2}
                  alt="Insurance Covered Loading"
                  width={85}
                  height={85}
                />
              </div>
            </div>
            <div className={s.row}>
              <div className={cx(s.image, s.image4)}>
                <Image
                  src={Tile3}
                  alt="Insurance Covered Loading"
                  width={85}
                  height={85}
                />
              </div>
              <div className={cx(s.image, s.image3)}>
                <Image
                  src={Tile4}
                  alt="Insurance Covered Loading"
                  width={85}
                  height={85}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }

    // Show the result — CoverageResult handles null client gracefully
    if (eligibilityStatus) {
      return <CoverageResult {...coverageResultProps} />
    }
  }

  return (
    <div>
      <h2 className={s.header}>{header}</h2>
      <div className={s.animationContainer}>
        <div className={s.row}>
          <div className={cx(s.image, s.image1)}>
            <Image
              src={Tile1}
              alt="Insurance Covered Loading"
              width={85}
              height={85}
            />
          </div>
          <div className={cx(s.image, s.image2)}>
            <Image
              src={Tile2}
              alt="Insurance Covered Loading"
              width={85}
              height={85}
            />
          </div>
        </div>
        <div className={s.row}>
          <div className={cx(s.image, s.image4)}>
            <Image
              src={Tile3}
              alt="Insurance Covered Loading"
              width={85}
              height={85}
            />
          </div>
          <div className={cx(s.image, s.image3)}>
            <Image
              src={Tile4}
              alt="Insurance Covered Loading"
              width={85}
              height={85}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default InsuranceCoveredLoading
